import { faMapLocationDot, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import isEqual from "fast-deep-equal/es6";
import React, { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { MoreToggle } from "../../components/MoreToggle";
import { cubeGeometry } from "../../state/cubeGeometry";
import {
  useOmgeving,
  type Analysis,
  type Omgeving as OmgevingData,
  type OmgevingDocument,
  type Regulation,
} from "../../state/omgeving";
import { Cube } from "../../state/types";
import {
  analysisText,
  articleLabel,
  mapLink,
  regulationLine,
  sortRegulations,
} from "./omgevingFormat";
import { A, Info, SubHeader } from "./util";

const SHOW = 5;

type PublicLinks = { url?: string | null; kaartUrl?: string | null };
const MapIcon = ({ ...props }) => <FontAwesomeIcon icon={faMapLocationDot} {...props} size="xs" />;

/** Titel naar de tekst, en Regels op de kaart ernaast als die ergens anders heen gaat. */
const Title = ({ item, children }: { item: PublicLinks; children: React.ReactNode }) => {
  const map = mapLink(item);
  return (
    <>
      {item.url ? <A href={item.url}>{children}</A> : children}
      {map && <A href={map} Icon={MapIcon}></A>}
    </>
  );
};

const Muted = ({ children }: { children: React.ReactNode }) => (
  <div className="w-full text-sm text-gray-600">{children}</div>
);

/** Tussenkop binnen het blok: groepeert zonder een eigen rand. */
const Group = ({ children }: { children: React.ReactNode }) => (
  <div className="px-2.5 py-1 bg-light-gray text-xs font-semibold uppercase tracking-wide text-gray-600">
    {children}
  </div>
);

/** Een rij over de volle breedte: de kop erboven, niet in een kolom ernaast. */
const Block = ({ heading, children }: { heading: string; children: React.ReactNode }) => (
  <div className="p-2.5">
    <div className="font-semibold">{heading}</div>
    {children}
  </div>
);

const List = <T,>({
  heading,
  rows,
  Row,
}: {
  heading: string;
  rows: T[];
  Row: React.FC<{ r: T }>;
}) => {
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  return (
    <Block heading={`${heading} (${rows.length})`}>
      {(open ? rows : rows.slice(0, SHOW)).map((r, i) => (
        // Regelingen en documenten hebben geen eigen id en de volgorde staat vast
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Row key={i} r={r} />
      ))}
      {rows.length > SHOW && <MoreToggle open={open} setOpen={setOpen} />}
    </Block>
  );
};

const RegulationRow = ({ r }: { r: Regulation }) => (
  <div className="py-1">
    <Title item={r}>{r.titel}</Title>
    <Muted>{regulationLine(r)}</Muted>
  </div>
);

const DocumentRow = ({ r }: { r: OmgevingDocument }) => (
  <div className="py-1">
    <Title item={r}>{r.titel}</Title>
    {r.soort && <Muted>{r.soort}</Muted>}
  </div>
);

const AnalysisLine = ({ a }: { a: Analysis }) => {
  const text = analysisText(a.analyse, a.resultaat);
  if (!text) return null;
  const article = articleLabel(a.artikel);
  const source = [a.regeling, article].filter(Boolean).join(", ");
  return (
    <div className="py-1">
      {a.analyse}: {text}
      {source && <Muted>{a.url ? <A href={a.url}>{source}</A> : source}</Muted>}
    </div>
  );
};

/** De rijen onder de uitklapregel, in hetzelfde blok: één geheel met één rand. */
const Content = ({ omgeving }: { omgeving: OmgevingData }) => {
  const regulations = sortRegulations(omgeving.regelingen ?? []);
  const documents = omgeving.documenten ?? [];
  const analyses = omgeving.analyses ?? [];
  const plans = omgeving.imro?.plannen ?? [];
  return (
    <>
      {(regulations.length > 0 || documents.length > 0) && (
        <>
          <Group>Regelingen en beleid (DSO)</Group>
          <List heading="Geldende regelingen" rows={regulations} Row={RegulationRow} />
          <List heading="Plannen en visies" rows={documents} Row={DocumentRow} />
        </>
      )}
      {(analyses.length > 0 || plans.length > 0) && (
        <>
          <Group>Regels op deze locatie</Group>
          {analyses.length > 0 && (
            <Block heading="Uit de regelteksten">
              {analyses.map((a, i) => (
                // Analyseregels uit het DSO: geen id, vaste volgorde
                // eslint-disable-next-line @eslint-react/no-array-index-key
                <AnalysisLine key={i} a={a} />
              ))}
            </Block>
          )}
          {/* Bestemming en bouwvlak staan al hierboven, uit de cube */}
          {plans.map((p, i) => (
            // Plannen hebben geen id in de samenvatting; de naam kan dubbel zijn
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Block key={i} heading={p.naam}>
              <Title item={p}>{[p.soort, p.status].filter(Boolean).join(", ")}</Title>
              {p.aanduidingen.length > 0 && <div>Aanduidingen: {p.aanduidingen.join("; ")}</div>}
              {p.gebiedsaanduidingen.length > 0 && (
                <div>Gebiedsaanduidingen: {p.gebiedsaanduidingen.join("; ")}</div>
              )}
              {p.maatvoeringen.length > 0 && <div>Maatvoering: {p.maatvoeringen.join("; ")}</div>}
              {p.analyses.map((a, j) => (
                // eslint-disable-next-line @eslint-react/no-array-index-key -- geen id, vaste volgorde
                <AnalysisLine key={j} a={a} />
              ))}
            </Block>
          ))}
        </>
      )}
    </>
  );
};

const isEmpty = (e?: OmgevingData) =>
  !e?.regelingen?.length && !e?.documenten?.length && !e?.imro?.plannen?.length;

/**
 * Wat het Omgevingsloket over deze plek weet, via de omgevingsmonitor:
 * regelingen, plannen en visies, en de regels die daaruit op de locatie
 * vallen. Bestemming en bouwvlak niet — die staan al in deze sectie uit de
 * cube, en dezelfde bestemming tweemaal laat de lezer raden welke telt.
 *
 * Dichtgeklapt tot de lezer erom vraagt: een vers gebied kost de monitor tot
 * 90 seconden. De regel staat er dus ook als er niets blijkt te zijn — dat is
 * de prijs van lui laden.
 */
export const Omgeving = ({ cube }: { cube: Cube }) => {
  const geometry = useMemo(() => cube?.geometry && cubeGeometry(cube), [cube]);
  // Open voor díé geometrie: klik je naar een ander perceel, dan klapt hij
  // dicht in plaats van meteen de volgende trage vraag af te vuren
  const [openFor, setOpenFor] = useState<GeoJSON.Geometry>();
  const open = !!geometry && isEqual(openFor, geometry);
  const [fetchedFor, state, omgeving, load] = useOmgeving(
    useShallow((s) => [s.geometry, s.state, s.omgeving, s.load])
  );

  useEffect(() => {
    if (open) load(geometry);
  }, [open, geometry, load]);

  if (!geometry) return null;
  // De store kan nog het antwoord voor het vorige perceel dragen
  const current = isEqual(fetchedFor, geometry);
  const done = current && (state === "fetched" || state === "error");
  return (
    <>
      <SubHeader>Omgevingsloket</SubHeader>
      <Info as="dl">
        {/* flex-wrap + w-full: de melding komt onder de knop, niet ernaast */}
        <div className="flex flex-wrap p-2.5">
          <span className="font-semibold">Regelingen, plannen en regels</span>
          <MoreToggle open={open} setOpen={(o: boolean) => setOpenFor(o ? geometry : undefined)} />
          {open && state === "fetching" && (
            <Muted>
              <FontAwesomeIcon icon={faSpinner} spin={true} fill="green" /> Ophalen bij het
              Omgevingsloket; dit kan een minuut duren…
            </Muted>
          )}
          {/* Een storing is stilte, net als bij de bekendmakingen */}
          {open && done && isEmpty(omgeving) && <Muted>Geen omgevingsinformatie gevonden.</Muted>}
        </div>
        {open && done && omgeving && <Content omgeving={omgeving} />}
      </Info>
    </>
  );
};
